import React, { useState } from "react";
import { Panel } from "@/components/common/primitives";
import { Check } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export function BillingPanel({ billing, onBillingUpdated }) {
  const plan = billing?.plan;
  const testMode = billing?.stripe_mode === "test" && !billing?.stripe_live;
  const billingAvailable = billing?.billing_available;
  const sandboxAvailable = billing?.sandbox_checkout_available !== false;
  const accessGranted = billing?.access_granted !== false;
  const [activating, setActivating] = useState(false);

  const activateSandbox = async (planId = "growth") => {
    if (activating) return;
    setActivating(true);
    try {
      const res = await api.sandboxCheckout(planId);
      const data = res?.data || res;
      toast.success(data?.message || "Sandbox entitlement granted — no charge.");
      if (typeof onBillingUpdated === "function") {
        await onBillingUpdated();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : err?.message || "Sandbox activate failed",
      );
    } finally {
      setActivating(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="settings-billing">
      {testMode && (
        <Panel className="p-4 border-amber-200 bg-amber-50">
          <div className="text-compact text-amber-800 font-medium">Sandbox billing</div>
          <p className="text-compact text-amber-700 mt-1">
            {billing?.message ||
              "Stripe live checkout is disabled. Access uses a non-chargeable sandbox entitlement until launch approval."}
          </p>
        </Panel>
      )}

      {plan?.name && (
        <Panel className="p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-stitle text-ink-primary">{plan.name}</div>
              <div className="text-compact text-ink-muted">
                Current plan: {plan.name}
                {billing?.entitlement_source
                  ? ` · ${billing.entitlement_source.replace(/_/g, " ")}`
                  : ""}
              </div>
            </div>
            {accessGranted && (
              <span className="text-micro font-mono text-signal-blue flex items-center gap-1">
                <Check className="h-3 w-3" /> Access granted
              </span>
            )}
          </div>
        </Panel>
      )}

      {sandboxAvailable && (
        <Panel className="p-4" testid="sandbox-checkout">
          <div className="text-compact text-ink-secondary">Sandbox checkout</div>
          <p className="text-compact text-ink-muted mt-1">
            Confirm Growth access without initiating a live charge.
          </p>
          <button
            type="button"
            data-testid="sandbox-activate-growth"
            disabled={activating}
            onClick={() => { void activateSandbox("growth"); }}
            className="mt-3 h-9 px-3 rounded-ctrl bg-emerald-600 text-white text-compact hover:bg-emerald-500 disabled:opacity-50"
          >
            {activating ? "Activating…" : "Activate sandbox Growth"}
          </button>
        </Panel>
      )}

      <Panel className="p-4">
        <div className="text-compact text-ink-secondary">Payment method</div>
        {billing?.payment_method ? (
          <div className="text-ink-primary mt-1 font-mono text-compact">
            {(billing.payment_method.brand || "Card").toUpperCase()} ·•••{" "}
            {billing.payment_method.last4}
          </div>
        ) : (
          <p className="text-compact text-ink-muted mt-1">
            {billingAvailable === false
              ? "Live payment methods disabled until launch approval."
              : "No payment method on file."}
          </p>
        )}
      </Panel>

      <Panel className="p-4">
        <div className="text-compact text-ink-secondary">Billing history</div>
        {billing?.invoices?.length > 0 ? (
          <ul className="mt-2 space-y-1 text-compact">
            {billing.invoices.map((inv) => (
              <li key={inv.id || inv.date}>{inv.description || inv.date}</li>
            ))}
          </ul>
        ) : (
          <p className="text-compact text-ink-muted mt-1">No invoices available.</p>
        )}
      </Panel>

      {!plan?.name && !billing?.payment_method && (
        <p className="text-compact text-ink-muted">Billing data unavailable.</p>
      )}
    </div>
  );
}

export function UsagePanel({ usage }) {
  if (!usage?.available) {
    return (
      <Panel className="p-4" testid="settings-usage">
        <p className="text-compact text-ink-secondary">
          {usage?.message || "Usage unavailable"}
        </p>
      </Panel>
    );
  }

  const rows = [
    ["Analyses", usage.analyses_used, usage.analyses_limit],
    ["Catalog rows", usage.catalog_rows_used, usage.catalog_rows_limit],
    ["Exports", usage.exports_used, usage.exports_limit],
  ];
  return (
    <Panel className="p-4 space-y-4" testid="settings-usage">
      {rows.map(([label, used, limit]) => (
        <div key={label}>
          <div className="flex justify-between text-compact mb-1">
            <span className="text-ink-secondary">{label}</span>
            <span className="font-mono">
              {Number(used || 0).toLocaleString()} /{" "}
              {limit == null ? "Unlimited" : Number(limit).toLocaleString()}
            </span>
          </div>
          {limit != null && (
            <div className="h-2 rounded bg-surface-2 overflow-hidden">
              <div
                className="h-full bg-emerald-500"
                style={{
                  width: `${Math.min(100, (Number(used) / Number(limit)) * 100)}%`,
                }}
              />
            </div>
          )}
        </div>
      ))}
    </Panel>
  );
}

export function DevicesPanel({ devices, onRevoke, onRevokeOthers }) {
  return (
    <Panel className="p-0 overflow-hidden" testid="settings-devices">
      <div className="hidden md:block">
        <table className="w-full text-compact">
          <thead>
            <tr className="text-micro font-mono uppercase text-ink-muted text-left bg-surface-2">
              <th className="py-2 px-3">Device</th>
              <th className="px-3">First seen</th>
              <th className="px-3">Last active</th>
              <th className="px-3">Status</th>
              <th className="px-3" />
            </tr>
          </thead>
          <tbody>
            {(devices || []).map((d) => (
              <DeviceRow key={d.session_id} device={d} onRevoke={onRevoke} />
            ))}
          </tbody>
        </table>
      </div>
      <div className="md:hidden p-3 space-y-2">
        {(devices || []).map((d) => (
          <DeviceCard key={d.session_id} device={d} onRevoke={onRevoke} />
        ))}
      </div>
      {devices?.length > 1 && (
        <div className="p-3 border-t border-edge-subtle">
          <button
            type="button"
            className="text-compact text-red-600"
            onClick={onRevokeOthers}
          >
            Sign out all other devices
          </button>
        </div>
      )}
    </Panel>
  );
}

function DeviceRow({ device, onRevoke }) {
  return (
    <tr className="border-b border-edge-subtle last:border-0">
      <td className="py-2 px-3">
        {device.device}
        {device.is_current && (
          <span className="ml-2 text-micro text-emerald-600">Current device</span>
        )}
      </td>
      <td className="px-3 font-mono text-micro">{device.first_seen_at || "—"}</td>
      <td className="px-3 font-mono text-micro">{device.last_active_at || "—"}</td>
      <td className="px-3 text-micro uppercase">{device.status}</td>
      <td className="px-3 text-right">
        {!device.is_current && device.status === "active" && (
          <button
            type="button"
            className="text-compact text-ink-muted"
            onClick={() => onRevoke?.(device.session_id)}
          >
            Sign out
          </button>
        )}
      </td>
    </tr>
  );
}

function DeviceCard({ device, onRevoke }) {
  return (
    <div className="rounded-ctrl border border-edge-subtle p-3">
      <div className="font-medium">{device.device}</div>
      {device.is_current && (
        <div className="text-micro text-emerald-600 mt-1">Current device</div>
      )}
      <div className="text-micro text-ink-muted mt-2">
        Last active {device.last_active_at || "—"}
      </div>
    </div>
  );
}

export function PreferencesPanel({ preferences, onSave, saving }) {
  const [form, setForm] = React.useState(preferences || {});

  React.useEffect(() => {
    setForm(preferences || {});
  }, [preferences]);

  function toggle(key) {
    setForm((f) => ({ ...f, [key]: !f[key] }));
  }

  return (
    <Panel className="p-4 space-y-4" testid="settings-preferences">
      {[
        ["reduced_motion", "Reduced motion"],
        ["compact_tables", "Compact tables"],
        ["show_provenance_tags", "Show provenance tags"],
        ["weekly_summary", "Weekly summary email"],
      ].map(([key, label]) => (
        <label key={key} className="flex items-center justify-between py-1 text-compact">
          <span>{label}</span>
          <input
            type="checkbox"
            checked={!!form[key]}
            onChange={() => toggle(key)}
          />
        </label>
      ))}

      <label className="block text-compact">
        <span className="text-ink-secondary">Default sourcing ZIP</span>
        <input
          className="mt-1 w-full max-w-xs h-9 px-3 rounded-ctrl border border-edge-subtle font-mono"
          value={form.default_sourcing_postal_code || ""}
          onChange={(e) =>
            setForm((f) => ({
              ...f,
              default_sourcing_postal_code: e.target.value,
            }))
          }
          placeholder="32822"
        />
      </label>

      <button
        type="button"
        className="h-9 px-3 rounded-ctrl bg-signal-blue text-white text-compact disabled:opacity-50"
        disabled={saving}
        onClick={() => onSave?.(form)}
      >
        {saving ? "Saving…" : "Save preferences"}
      </button>
    </Panel>
  );
}
